import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback } from '@theme/utilities';

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.variantChanged.bind(this));
    if (this.dataset.templateProductMatch === 'true' && !this.closest('product-card, quick-add-dialog')) {
      this.addEventListener('click', event => {
        const input = event.target.closest('input[type="radio"]') || event.target.closest('label')?.querySelector('input[type="radio"]');
        if (!input) { event.stopPropagation(); return; }
        event.preventDefault();
        event.stopImmediatePropagation();
        setTimeout(() => { input.checked = true; this.variantChanged({ target: input, stopPropagation() {} }); }, 0);
      }, true);
      const section = this.closest('.shopify-section');
      const guard = () => {
        const incomplete = Array.from(this.querySelectorAll('fieldset, select')).some(group => group instanceof HTMLSelectElement ? !group.value : !group.querySelector('input:checked'));
        if (incomplete || this.dataset.ohanaResolving === 'true') {
          section?.querySelectorAll('[ref="addToCartButton"], #customily-cart-btn').forEach(button => { if (!button.disabled) button.disabled = true; });
        }
      };
      this.ohanaGuard = new MutationObserver(guard);
      this.ohanaGuard.observe(section, { subtree: true, childList: true, attributes: true, attributeFilter: ['disabled', 'checked', 'data-ohana-resolving'] });
      section?.addEventListener('click', event => {
        if (!event.target.closest('button[name="add"]')) return;
        const incomplete = Array.from(this.querySelectorAll('fieldset, select')).some(group => group instanceof HTMLSelectElement ? !group.value : !group.querySelector('input:checked'));
        if (incomplete || this.dataset.ohanaResolving === 'true') { event.preventDefault(); event.stopImmediatePropagation(); guard(); }
      }, true);
      section?.addEventListener('submit', event => {
        const incomplete = Array.from(this.querySelectorAll('fieldset, select')).some(group => group instanceof HTMLSelectElement ? !group.value : !group.querySelector('input:checked'));
        if (incomplete || this.dataset.ohanaResolving === 'true') { event.preventDefault(); event.stopImmediatePropagation(); guard(); }
      }, true);
      this.addEventListener('change', guard);
      guard();
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  #ohanaPreviewController;

  async previewPendingColor(option) {
    if (!option.dataset.previewVariantId) return;
    this.#ohanaPreviewController?.abort();
    const controller = new AbortController();
    this.#ohanaPreviewController = controller;
    const url = new URL(this.dataset.productUrl, location.origin);
    url.searchParams.set('variant', option.dataset.previewVariantId);
    try {
      const response = await fetch(url.toString(), { signal: controller.signal });
      if (!response.ok) return;
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (this.dataset.ohanaPending !== 'true' || controller.signal.aborted) return;
      const replacement = html.querySelector('main media-gallery');
      if (replacement) this.closest('.shopify-section')?.querySelector('media-gallery')?.replaceWith(replacement);
    } catch (error) {
      if (error.name !== 'AbortError') console.error(error);
    }
  }

  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    if (this.dataset.templateProductMatch === 'true') event.stopPropagation();
    this.updateSelectedOption(event.target);
    if (this.dataset.ohanaPending === 'true') {
      const groups = Array.from(this.querySelectorAll('fieldset, select'));
      const complete = groups.every(group => group instanceof HTMLSelectElement ? !!group.value : !!group.querySelector('input:checked'));
      const label = selectedOption.closest('fieldset')?.querySelector('.variant-option__swatch-value');
      if (label) label.textContent = selectedOption.value;
      if (!complete) {
        this.previewPendingColor(selectedOption);
        return;
      }
      this.#ohanaPreviewController?.abort();
      delete this.dataset.ohanaPending;
      this.querySelector('[data-ohana-selection-status]')?.remove();
    }
    this.dataset.ohanaResolving = 'true';
    this.dispatchEvent(new VariantSelectedEvent({ id: selectedOption.dataset.optionValueId ?? '' }));

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;

    let requestUrl = this.buildRequestUrl(selectedOption);
    const variantsData = this.querySelector('[data-ohana-variants]');
    if (isOnProductPage && variantsData && !loadsNewProduct) {
      const chosen = Array.from(this.querySelectorAll('fieldset, select')).map(group => group instanceof HTMLSelectElement ? group.value : group.querySelector('input:checked')?.value);
      const variant = JSON.parse(variantsData.textContent).find(item => chosen.every((value, index) => value === item.options[index]));
      if (!variant) return;
      requestUrl = this.dataset.productUrl.split('?')[0] + '?variant=' + variant.id;
    }
    this.fetchUpdatedSection(requestUrl, loadsNewProduct);

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) url.searchParams.delete('variant');

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (this.closest('quick-add-component') || this.closest('swatches-variant-picker-component')) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join('&')}`;
    }
    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const requestSignal = this.#abortController.signal;
    fetch(requestUrl, { signal: requestSignal })
      .then((response) => response.text())
      .then(async (responseText) => {
        this.#pendingRequestUrl = undefined;
        let html = new DOMParser().parseFromString(responseText, 'text/html');
        const pendingPicker = html.querySelector('variant-picker[data-ohana-pending="true"]');
        const candidate = JSON.parse(html.querySelector('variant-picker script[type="application/json"]:not([data-ohana-variants])')?.textContent || 'null');
        if (pendingPicker && candidate?.id && new URL(requestUrl, location.origin).searchParams.has('option_values')) {
          const chosen = Array.from(this.querySelectorAll('fieldset, select')).map(group => group instanceof HTMLSelectElement ? group.value : group.querySelector('input:checked')?.value);
          if (!candidate.options || chosen.some((value, index) => value !== candidate.options[index])) throw new Error('Variant selection does not match');
          if (requestSignal.aborted) return;
          const resolvedRequest = new URL(requestUrl, location.origin);
          resolvedRequest.searchParams.delete('option_values');
          resolvedRequest.searchParams.set('variant', candidate.id);
          const resolvedResponse = await fetch(resolvedRequest.toString(), { signal: requestSignal });
          if (!resolvedResponse.ok) throw new Error('Variant could not be loaded');
          html = new DOMParser().parseFromString(await resolvedResponse.text(), 'text/html');
        }
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const textContent = html.querySelector(`variant-picker script[type="application/json"]:not([data-ohana-variants])`)?.textContent;
        if (!textContent) return;
        const resolvedVariant = JSON.parse(textContent);
        if (this.dataset.templateProductMatch === 'true' && !this.closest('product-card, quick-add-dialog')) {
          const resolvedUrl = new URL(location.href);
          if (resolvedVariant?.id) resolvedUrl.searchParams.set('variant', resolvedVariant.id);
          else resolvedUrl.searchParams.delete('variant');
          history.replaceState({}, '', resolvedUrl);
        }

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          delete this.dataset.ohanaResolving;
          this.closest('.shopify-section')?.querySelectorAll('#customily-cart-btn').forEach(button => { button.disabled = !resolvedVariant?.available; });
          // We grab the variant object from the response and dispatch an event with it.
          if (this.selectedOptionId) {
            this.dispatchEvent(
              new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
              })
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.warn('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
